import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const ProspectionTemplates = ({ business, websiteAnalysis }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('performance');
  const [customMessage, setCustomMessage] = useState('');
  const [copiedText, setCopiedText] = useState('');

  if (!business || !websiteAnalysis) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Analysez d'abord un site web pour accéder aux templates
        </Typography>
      </Box>
    );
  }

  const templates = {
    performance: {
      title: '🚀 Problème de Performance',
      icon: <SpeedIcon />,
      subject: `Votre site web ${business.name} perd des clients à cause de sa lenteur`,
      message: `Bonjour ${business.name},

J'ai analysé votre site web ${business.website} et j'ai identifié un problème critique qui vous fait perdre des clients : votre site met plus de 3 secondes à se charger.

📊 Les chiffres parlent d'eux-mêmes :
• 53% des visiteurs quittent un site qui met plus de 3 secondes à charger
• Chaque seconde de délai réduit les conversions de 7%
• Google pénalise les sites lents dans ses résultats

🚨 Impact sur votre business :
• Perte de 40% des conversions potentielles
• Mauvaise image de marque
• Positionnement dégradé sur Google

✅ Ma solution :
Optimisation complète pour un chargement en moins de 2 secondes, ce qui peut augmenter vos conversions de +150%.

🎯 Je vous propose un audit gratuit de 15 minutes pour identifier précisément les actions à mettre en place.

Êtes-vous disponible cette semaine pour en discuter ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    },

    security: {
      title: '🔒 Problème de Sécurité',
      icon: <SecurityIcon />,
      subject: `Sécurité critique : votre site web ${business.name} présente des vulnérabilités`,
      message: `Bonjour ${business.name},

Suite à l'analyse de votre site web ${business.website}, j'ai détecté des vulnérabilités de sécurité qui peuvent compromettre vos données et celles de vos clients.

🚨 Problèmes identifiés :
• Pas de certificat SSL/HTTPS
• Headers de sécurité manquants
• Données sensibles potentiellement exposées

💼 Impact business :
• Risque de piratage et vol de données
• Perte de confiance des clients
• Sanctions possibles (RGPD)
• Image de marque dégradée

✅ Ma solution :
Sécurisation complète avec certificat SSL et protection avancée pour rétablir la confiance.

🎯 Je vous propose un audit de sécurité gratuit pour évaluer les risques et proposer un plan d'action.

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    },

    seo: {
      title: '📈 Problème de Visibilité Google',
      icon: <VisibilityIcon />,
      subject: `Votre site ${business.name} n'apparaît pas sur Google - vous perdez 90% du trafic`,
      message: `Bonjour ${business.name},

J'ai analysé votre site web ${business.website} et j'ai constaté que vous n'apparaissez pas dans les premiers résultats Google pour vos mots-clés.

📊 La réalité :
• 90% du trafic vient de Google
• 75% des utilisateurs ne dépassent pas la première page
• Votre site est invisible pour vos prospects

🚨 Problèmes identifiés :
• Meta descriptions manquantes
• Structure des titres incorrecte
• Pas de sitemap XML
• URLs non optimisées

💼 Impact business :
• Perte de 90% du trafic organique potentiel
• Concurrence qui vous devance
• Investissements marketing inefficaces

✅ Ma solution :
Optimisation SEO complète pour apparaître en première page et multiplier votre trafic par 3.

🎯 Je vous propose un audit SEO gratuit pour identifier les actions prioritaires.

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    },

    mobile: {
      title: '📱 Problème Mobile',
      icon: <MobileIcon />,
      subject: `65% de vos visiteurs sont sur mobile mais votre site ${business.name} n'est pas adapté`,
      message: `Bonjour ${business.name},

J'ai analysé votre site web ${business.website} et j'ai constaté qu'il n'est pas optimisé pour mobile, alors que 65% de vos visiteurs utilisent leur smartphone.

📊 Les chiffres :
• 65% du trafic web vient du mobile
• 57% des utilisateurs ne recommandent pas un site mal adapté
• Google favorise les sites mobiles

🚨 Problèmes identifiés :
• Site non responsive
• Boutons trop petits
• Navigation difficile
• Texte illisible

💼 Impact business :
• Perte de 65% des visiteurs mobiles
• Mauvaise expérience utilisateur
• Positionnement dégradé sur Google

✅ Ma solution :
Design responsive professionnel pour capturer tous vos visiteurs mobiles.

🎯 Je vous propose un audit mobile gratuit pour évaluer les améliorations nécessaires.

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    },

    ecommerce: {
      title: '🛒 Problème E-commerce',
      icon: <EcommerceIcon />,
      subject: `Votre boutique en ligne ${business.name} ne convertit pas - vous perdez des ventes`,
      message: `Bonjour ${business.name},

J'ai analysé votre boutique en ligne ${business.website} et j'ai identifié plusieurs problèmes qui limitent vos ventes.

📊 Votre situation :
• Taux de conversion < 2% (norme : 3-5%)
• Panier abandonné à 68%
• Processus d'achat compliqué

🚨 Problèmes identifiés :
• Tunnel de vente inefficace
• Fiches produits incomplètes
• Moyens de paiement limités
• Pas de confiance client

💼 Impact business :
• Perte de 60% des ventes potentielles
• Investissements marketing gaspillés
• Concurrence qui vous devance

✅ Ma solution :
Optimisation complète du tunnel de vente pour doubler vos conversions.

🎯 Je vous propose un audit e-commerce gratuit pour identifier les points d'amélioration.

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    },

    general: {
      title: '🎯 Audit Général',
      icon: <BusinessIcon />,
      subject: `Audit gratuit : ${business.name} - ${websiteAnalysis.overallScore}/100`,
      message: `Bonjour ${business.name},

J'ai analysé votre site web ${business.website} et j'ai identifié plusieurs axes d'amélioration qui impactent votre business.

📊 Mon évaluation :
• Score global : ${websiteAnalysis.overallScore}/100
• ${websiteAnalysis.criticalIssues || 0} problème${websiteAnalysis.criticalIssues > 1 ? 's' : ''} critique${websiteAnalysis.criticalIssues > 1 ? 's' : ''}
• Potentiel d'amélioration : +150% à +300% de conversions

🚨 Points critiques identifiés :
${websiteAnalysis.criticalIssues ? `• ${websiteAnalysis.criticalIssues} problème${websiteAnalysis.criticalIssues > 1 ? 's' : ''} nécessitant une action immédiate` : '• Plusieurs axes d\'amélioration identifiés'}

💼 Impact sur votre business :
• Perte de clients et de revenus
• Image de marque dégradée
• Concurrence qui vous devance

✅ Ma solution :
Plan d'action personnalisé pour optimiser votre site et maximiser vos résultats.

🎯 Je vous propose un audit détaillé gratuit pour identifier précisément les actions à mettre en place.

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const getTemplateSuggestions = () => {
    const suggestions = [];
    
    if (websiteAnalysis.performance?.performanceScore < 70) {
      suggestions.push('performance');
    }
    if (websiteAnalysis.security?.securityScore < 80) {
      suggestions.push('security');
    }
    if (websiteAnalysis.seo?.seoScore < 70) {
      suggestions.push('seo');
    }
    if (websiteAnalysis.mobile?.mobileScore < 75) {
      suggestions.push('mobile');
    }
    if (websiteAnalysis.ecommerce?.ecommerceScore < 70) {
      suggestions.push('ecommerce');
    }
    
    return suggestions.length > 0 ? suggestions : ['general'];
  };

  const selectedTemplateData = templates[selectedTemplate];
  const suggestions = getTemplateSuggestions();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon color="primary" />
        Templates d'Emails de Prospection
      </Typography>

      {/* Suggestions de templates */}
      <Card sx={{ mb: 3, bgcolor: 'info.light', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Templates Recommandés
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Basé sur l'analyse de votre site, voici les templates les plus pertinents :
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {suggestions.map((template) => (
              <Chip
                key={template}
                label={templates[template].title}
                onClick={() => setSelectedTemplate(template)}
                color={selectedTemplate === template ? 'secondary' : 'default'}
                variant={selectedTemplate === template ? 'filled' : 'outlined'}
                sx={{ color: selectedTemplate === template ? 'white' : 'inherit' }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Sélection du template */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Choisir un Template
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Type de message</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  label="Type de message"
                >
                  {Object.entries(templates).map(([key, template]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {template.icon}
                        {template.title}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedTemplateData.icon}
                {selectedTemplateData.title}
              </Typography>

              {/* Sujet */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Sujet de l'email :
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    value={selectedTemplateData.subject}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title="Copier le sujet">
                    <IconButton 
                      onClick={() => copyToClipboard(selectedTemplateData.subject, 'sujet')}
                      color={copiedText === 'sujet' ? 'success' : 'primary'}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Message */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Corps du message :
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={selectedTemplateData.message}
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title="Copier le message">
                    <IconButton 
                      onClick={() => copyToClipboard(selectedTemplateData.message, 'message')}
                      color={copiedText === 'message' ? 'success' : 'primary'}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Message personnalisé */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Message personnalisé (optionnel) :
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Ajoutez un message personnalisé..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  variant="outlined"
                />
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => copyToClipboard(selectedTemplateData.message + (customMessage ? '\n\n' + customMessage : ''), 'complet')}
                  size="large"
                >
                  Copier le Message Complet
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conseils d'utilisation */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Conseils pour Maximiser vos Réponses
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Personnalisez le message" 
                secondary="Ajoutez des détails spécifiques à leur secteur d'activité"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Envoyez au bon moment" 
                secondary="Mardi à jeudi, entre 9h et 11h ou 14h et 16h"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Suivez vos prospects" 
                secondary="Relancez 3-5 jours après si pas de réponse"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Préparez votre pitch" 
                secondary="Ayez des exemples concrets de gains pour votre secteur"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {copiedText && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {copiedText === 'sujet' ? 'Sujet copié !' : 
           copiedText === 'message' ? 'Message copié !' : 
           'Message complet copié !'}
        </Alert>
      )}
    </Box>
  );
};

export default ProspectionTemplates;